import React, { useState, useEffect, useRef } from 'react';
import { useLiveData } from '../../hooks/useLiveData';
import { ipApi } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { useHmsConfig } from '../../hooks/useHmsRecords';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function calcStay(admDate, dischargeDate) {
  if (!admDate || !dischargeDate) return 0;
  const diff = new Date(dischargeDate) - new Date(admDate);
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

function emptyMedRow() {
  return { drug: '', dose: '', frequency: '', duration: '', instructions: '' };
}

function buildInitialForm(patient) {
  return {
    // Section 1
    patientName:   patient?.name    || '',
    ageGender:     patient ? `${patient.age}Y / ${patient.gender === 'M' ? 'Male' : 'Female'}` : '',
    uhid:          patient?.uhid    || '',
    bed:           patient?.bed     || '',
    ward:          patient?.ward    || '',
    admissionDate: patient?.admDate || '',
    dischargeDate: todayStr(),
    // Section 2
    chiefComplaint:     '',
    provisionalDiagnosis: patient?.diagnosis || '',
    finalDiagnosis:     '',
    comorbidities:      '',
    historyHPI:         '',
    examinationFindings:'',
    // Section 3
    labSummary:         '',
    radiologyFindings:  '',
    ecgSpecialTests:    '',
    // Section 4
    proceduresPerformed:'',
    ivMedications:      '',
    bloodProducts:      'Nil',
    physiotherapy:      '',
    // Section 5
    conditionAtDischarge: 'Stable',
    dischargeType:        'Regular',
    referredTo:           '',
    // Section 6 – medications table handled separately
    // Section 7
    followupDate:          '',
    followupDoctor:        '',
    followupInstructions:  '',
    dietAdvice:            '',
    activityRestrictions:  '',
    // Section 8
    preparedBy:            '',
    designation:           '',
    reviewedBy:            '',
    reviewDesignation:     '',
    summaryDate:           todayStr(),
  };
}

// ─── Print style injection ───────────────────────────────────────────────────
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #discharge-print-area, #discharge-print-area * { visibility: visible; }
  #discharge-print-area { position: absolute; top: 0; left: 0; width: 100%; }
  .left-panel-col { display: none !important; }
  .action-bar     { display: none !important; }
  .right-panel-col {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .discharge-page-wrap {
    display: block !important;
  }
  .print-header {
    display: block !important;
    text-align: center;
    margin-bottom: 12px;
    border-bottom: 2px solid #000;
    padding-bottom: 8px;
  }
  .print-header h2 { font-size: 16pt; margin: 0; }
  .print-header p  { font-size: 10pt; margin: 2px 0; }
  .discharge-section {
    border-bottom: 1px dashed #555;
    padding-bottom: 10px;
    margin-bottom: 12px;
    page-break-inside: avoid;
  }
  .section-title {
    font-size: 11pt;
    font-weight: bold;
    margin-bottom: 6px;
    background: #eee !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  input, textarea, select {
    border: none !important;
    background: transparent !important;
    font-size: 11pt !important;
    resize: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
  }
  table { font-size: 10pt !important; }
  @page {
    size: A4 portrait;
    margin: 1cm;
  }
}
@media screen {
  .print-header { display: none; }
}
`;

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function DischargeSummaryPage() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { config: aiCfg } = useHmsConfig('ai-settings');
  const hospitalAiOn = aiCfg?.enabled !== false;
  const hospitalName = hospital?.name || user?.hospital || 'Hospital';

  const { data, loading, error, reload } = useLiveData(() => ipApi.getAdmissions(80), []);
  const summaryIndex = useLiveData(() => ipApi.getDischargeSummaries(80), []);
  const summaryByAdmission = React.useMemo(() => {
    const map = {};
    (summaryIndex.data?.summaries || []).forEach((s) => { map[s.admissionId] = s; });
    return map;
  }, [summaryIndex.data]);

  const patients = React.useMemo(() => {
    const list = data?.admissions || [];
    return list.map((a) => ({
      id: a.admissionId,
      admissionId: a.admissionId,
      patientId: a.patientId,
      name: a.patientName || 'Unknown',
      age: a.age || '',
      gender: a.gender || 'M',
      uhid: a.patientId || '',
      bed: a.bedLabel || a.bedId || '',
      ward: a.ward || '',
      admDate: (a.admittedAt || '').slice(0, 10),
      diagnosis: a.reason || a.admittingDiagnosis || '',
      doctor: a.doctor || '',
    }));
  }, [data]);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [form,            setForm]            = useState(null);
  const [medications,     setMedications]     = useState([emptyMedRow(), emptyMedRow()]);
  const [savedMsg,        setSavedMsg]        = useState('');
  const [saving,          setSaving]          = useState(false);
  const [summaryStatus,   setSummaryStatus]   = useState('');

  const printRef = useRef(null);

  // ── Inject print CSS once ────────────────────────────────────────────────
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id    = 'discharge-print-styles';
    styleEl.textContent = PRINT_CSS;
    if (!document.getElementById('discharge-print-styles')) {
      document.head.appendChild(styleEl);
    }
    return () => {
      const el = document.getElementById('discharge-print-styles');
      if (el) el.remove();
    };
  }, []);

  // ── Select patient ────────────────────────────────────────────────────────
  async function selectPatient(p) {
    setSelectedPatient(p);
    setSavedMsg('');
    setSummaryStatus('');
    const cached = summaryByAdmission[p.admissionId];
    if (cached?.form) {
      setForm(cached.form);
      setMedications(cached.medications?.length ? cached.medications : [emptyMedRow(), emptyMedRow()]);
      setSummaryStatus(cached.status || 'draft');
      return;
    }
    try {
      const res = await ipApi.getDischargeSummary(p.admissionId);
      if (res?.summary?.form) {
        setForm(res.summary.form);
        setMedications(res.summary.medications?.length ? res.summary.medications : [emptyMedRow(), emptyMedRow()]);
        setSummaryStatus(res.summary.status || 'draft');
        return;
      }
    } catch (_) { /* no saved summary yet */ }
    // Enterprise auto-generate from EMR + labs + meds (Epic/Cerner style)
    try {
      setSavedMsg('Auto-filling discharge summary from chart…');
      const gen = await ipApi.generateDischargeSummary(p.admissionId);
      if (gen?.summary?.form) {
        setForm(gen.summary.form);
        setMedications(gen.summary.medications?.length ? gen.summary.medications : [emptyMedRow(), emptyMedRow()]);
        setSummaryStatus('draft');
        setSavedMsg('Draft auto-generated from EMR, labs & pharmacy — review and finalize.');
        await summaryIndex.reload();
        return;
      }
    } catch (_) { /* fall back to blank */ }
    setForm(buildInitialForm(p));
    setMedications([emptyMedRow(), emptyMedRow()]);
    setSummaryStatus('');
    setSavedMsg('');
  }

  async function autoGenerate() {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      const gen = await ipApi.generateDischargeSummary(selectedPatient.admissionId);
      if (gen?.summary?.form) {
        setForm(gen.summary.form);
        setMedications(gen.summary.medications?.length ? gen.summary.medications : [emptyMedRow(), emptyMedRow()]);
        setSummaryStatus('draft');
        setSavedMsg('Re-generated from latest EMR, labs & pharmacy data.');
        await summaryIndex.reload();
      }
    } catch (err) {
      alert(err.message || 'Auto-generate failed');
    } finally {
      setSaving(false);
    }
  }

  async function aiPolishDraft() {
    if (!selectedPatient || !form) return;
    setSaving(true);
    try {
      const res = await ipApi.aiDischargeDraft({ form, medications });
      if (res?.draft) {
        setForm((prev) => ({ ...prev, ...res.draft }));
        setSavedMsg(res.disclaimer || 'AI draft applied — review carefully before finalize.');
      }
    } catch (err) {
      const msg = err.message || 'AI draft failed';
      if (msg.includes('not configured') || msg.includes('AI_NOT_CONFIGURED')) {
        alert('AI is not configured yet.\n\nAsk your HCEN admin to set the AI API key on the HCEN_IP_API Lambda, then try again.');
      } else {
        alert(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  async function persistSummary(status) {
    if (!selectedPatient || !form) return;
    setSaving(true);
    try {
      await ipApi.saveDischargeSummary({
        admissionId: selectedPatient.admissionId,
        status,
        form,
        medications,
        totalStay,
      });
      await summaryIndex.reload();
      setSummaryStatus(status);
      setSavedMsg(status === 'final' ? 'Discharge summary finalized and saved.' : 'Draft saved to server.');
      setTimeout(() => setSavedMsg(''), 4000);
      if (status === 'final') {
        const ok = window.confirm('Finalize IPD discharge for this patient? Bed will be marked vacant.');
        if (ok) {
          await ipApi.discharge(selectedPatient.admissionId);
          await reload();
          setSavedMsg('Summary finalized and patient discharged.');
        }
      }
    } catch (err) {
      alert(err.message || 'Failed to save discharge summary');
    } finally {
      setSaving(false);
    }
  }

  // ── Form change handler ───────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  // ── Medication table handlers ─────────────────────────────────────────────
  function handleMedChange(idx, field, value) {
    setMedications((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function addMedRow() {
    setMedications((prev) => [...prev, emptyMedRow()]);
  }

  function removeMedRow(idx) {
    setMedications((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Auto-calc total stay ──────────────────────────────────────────────────
  const totalStay = form ? calcStay(form.admissionDate, form.dischargeDate) : 0;

  // ── Filtered patient list ─────────────────────────────────────────────────
  const filteredPatients = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.uhid || '').toLowerCase().includes(q)
    );
  });

  // ── Save Draft ────────────────────────────────────────────────────────────
  function handleSaveDraft() {
    persistSummary('draft');
  }

  function handleFinalize() {
    persistSummary('final');
  }

  // ── Print ─────────────────────────────────────────────────────────────────
  function handlePrint() {
    window.print();
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.pageWrap} className="discharge-page-wrap">

      {/* ══ LEFT PANEL ══════════════════════════════════════════════════════ */}
      <div style={styles.leftPanel} className="left-panel-col">
        <div style={styles.leftHeader}>
          <h2 style={styles.leftTitle}>IPD Patients</h2>
        </div>

        {/* Search */}
        <div style={styles.searchWrap}>
          <input
            style={styles.searchInput}
            type="text"
            placeholder="Search by Name / UHID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {error && <p style={{ ...styles.emptyMsg, padding: '0 12px' }}>{error}</p>}

        {/* Patient list */}
        <div style={styles.patientList}>
          {loading ? (
            <div style={styles.spinnerWrap}>
              <div style={styles.spinner} />
              <span style={styles.spinnerText}>Loading patients…</span>
            </div>
          ) : filteredPatients.length === 0 ? (
            <p style={styles.emptyMsg}>No active IPD admissions. Admit a patient from IPD first.</p>
          ) : (
            filteredPatients.map((p) => {
              const isSelected = selectedPatient?.id === p.id;
              const hasSummary = summaryByAdmission[p.admissionId];
              return (
                <div
                  key={p.id}
                  style={{ ...styles.patientCard, ...(isSelected ? styles.patientCardActive : {}) }}
                  onClick={() => selectPatient(p)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && selectPatient(p)}
                >
                  <div style={styles.cardTop}>
                    <span style={styles.cardName}>{p.name}</span>
                    <span style={styles.cardBadge}>{p.bed || '—'}</span>
                  </div>
                  <div style={styles.cardMeta}>
                    <span style={styles.cardUhid}>{p.uhid}</span>
                    <span style={styles.cardWard}>{p.ward}</span>
                    {hasSummary && (
                      <span style={{ fontSize: 10, color: hasSummary.status === 'final' ? '#34d399' : '#fbbf24', fontWeight: 700 }}>
                        {hasSummary.status === 'final' ? 'Final' : 'Draft'}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardDiag}>{p.diagnosis || '—'}</div>
                  <div style={styles.cardAdmDate}>Adm: {p.admDate}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ══ RIGHT PANEL ═════════════════════════════════════════════════════ */}
      <div style={styles.rightPanel} className="right-panel-col">

        {/* Print header (visible only on print) */}
        <div className="print-header">
          <h2>{hospitalName}</h2>
          <p><strong>DISCHARGE SUMMARY</strong></p>
          <p>Printed on: {new Date().toLocaleString()}</p>
        </div>

        {!selectedPatient ? (
          <div style={styles.placeholder}>
            <div style={styles.placeholderIcon}>🏥</div>
            <h3 style={styles.placeholderTitle}>Select an IPD Patient</h3>
            <p style={styles.placeholderSub}>
              Choose a patient from the left panel to fill the NABH discharge summary.
            </p>
          </div>
        ) : (
          <div ref={printRef} id="discharge-print-area" style={styles.formWrap}>

            <div style={styles.formHeader}>
              <div>
                <h2 style={styles.formTitle}>Discharge Summary</h2>
                <p style={styles.formSubtitle}>NABH Compliant · {selectedPatient.name}</p>
              </div>
              {summaryStatus === 'final' ? (
                <span style={{ ...styles.draftBadge, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' }}>Finalized</span>
              ) : summaryStatus === 'draft' ? (
                <span style={styles.draftBadge}>Draft Saved</span>
              ) : null}
            </div>

            {/* ── SECTION 1: Patient Information ────────────────────────── */}
            <Section title="1. Patient Information" icon="👤">
              <div style={styles.grid2}>
                <Field label="Patient Name">
                  <input style={styles.input} name="patientName" value={form.patientName} onChange={handleChange} />
                </Field>
                <Field label="Age / Gender">
                  <input style={styles.input} name="ageGender" value={form.ageGender} onChange={handleChange} />
                </Field>
                <Field label="UHID">
                  <input style={styles.input} name="uhid" value={form.uhid} onChange={handleChange} />
                </Field>
                <Field label="Bed">
                  <input style={styles.input} name="bed" value={form.bed} onChange={handleChange} />
                </Field>
                <Field label="Ward">
                  <input style={styles.input} name="ward" value={form.ward} onChange={handleChange} />
                </Field>
                <Field label="Admission Date">
                  <input style={styles.input} type="date" name="admissionDate" value={form.admissionDate} onChange={handleChange} />
                </Field>
                <Field label="Discharge Date">
                  <input style={styles.input} type="date" name="dischargeDate" value={form.dischargeDate} onChange={handleChange} />
                </Field>
                <Field label="Total Stay">
                  <input style={{ ...styles.input, background: '#f0f4f8', cursor: 'default' }} value={`${totalStay} day${totalStay !== 1 ? 's' : ''}`} readOnly />
                </Field>
              </div>
            </Section>

            {/* ── SECTION 2: Clinical Summary ───────────────────────────── */}
            <Section title="2. Clinical Summary" icon="📝">
              <div style={styles.grid2}>
                <Field label="Chief Complaint" fullWidth>
                  <textarea style={styles.textarea} name="chiefComplaint" rows={3} value={form.chiefComplaint} onChange={handleChange} placeholder="Primary presenting complaint…" />
                </Field>
                <Field label="Provisional Diagnosis">
                  <input style={styles.input} name="provisionalDiagnosis" value={form.provisionalDiagnosis} onChange={handleChange} />
                </Field>
                <Field label="Final Diagnosis">
                  <input style={styles.input} name="finalDiagnosis" value={form.finalDiagnosis} onChange={handleChange} placeholder="ICD-10 code if available" />
                </Field>
                <Field label="Co-morbidities" fullWidth>
                  <input style={styles.input} name="comorbidities" value={form.comorbidities} onChange={handleChange} placeholder="DM, HTN, CKD, etc." />
                </Field>
                <Field label="History of Present Illness" fullWidth>
                  <textarea style={styles.textarea} name="historyHPI" rows={4} value={form.historyHPI} onChange={handleChange} placeholder="Detailed HPI…" />
                </Field>
                <Field label="Examination Findings" fullWidth>
                  <textarea style={styles.textarea} name="examinationFindings" rows={4} value={form.examinationFindings} onChange={handleChange} placeholder="Vitals, General examination, Systemic examination…" />
                </Field>
              </div>
            </Section>

            {/* ── SECTION 3: Investigations ─────────────────────────────── */}
            <Section title="3. Investigations" icon="🔬">
              <div style={styles.grid1}>
                <Field label="Lab Summary" fullWidth>
                  <textarea style={styles.textarea} name="labSummary" rows={4} value={form.labSummary} onChange={handleChange} placeholder="Key lab values, CBC, LFT, RFT, Electrolytes, Blood Sugar…" />
                </Field>
                <Field label="Radiology Findings" fullWidth>
                  <textarea style={styles.textarea} name="radiologyFindings" rows={3} value={form.radiologyFindings} onChange={handleChange} placeholder="X-Ray, USG, CT, MRI findings…" />
                </Field>
                <Field label="ECG / Special Tests" fullWidth>
                  <textarea style={styles.textarea} name="ecgSpecialTests" rows={3} value={form.ecgSpecialTests} onChange={handleChange} placeholder="ECG, Echo, Biopsy, Endoscopy, etc." />
                </Field>
              </div>
            </Section>

            {/* ── SECTION 4: Treatment Given ────────────────────────────── */}
            <Section title="4. Treatment Given" icon="💊">
              <div style={styles.grid2}>
                <Field label="Procedures Performed" fullWidth>
                  <textarea style={styles.textarea} name="proceduresPerformed" rows={3} value={form.proceduresPerformed} onChange={handleChange} placeholder="Surgeries, bedside procedures…" />
                </Field>
                <Field label="IV Medications" fullWidth>
                  <textarea style={styles.textarea} name="ivMedications" rows={4} value={form.ivMedications} onChange={handleChange} placeholder="IV fluids, antibiotics, other medications administered…" />
                </Field>
                <Field label="Blood Products">
                  <input style={styles.input} name="bloodProducts" value={form.bloodProducts} onChange={handleChange} placeholder="Nil or e.g. 2 PRBC" />
                </Field>
                <Field label="Physiotherapy">
                  <input style={styles.input} name="physiotherapy" value={form.physiotherapy} onChange={handleChange} placeholder="Nil / ROM exercises / Chest physio…" />
                </Field>
              </div>
            </Section>

            {/* ── SECTION 5: Condition at Discharge ────────────────────── */}
            <Section title="5. Condition at Discharge" icon="🩺">
              <div style={styles.grid2}>
                <Field label="Condition">
                  <select style={styles.select} name="conditionAtDischarge" value={form.conditionAtDischarge} onChange={handleChange}>
                    <option value="Stable">Stable</option>
                    <option value="Improved">Improved</option>
                    <option value="DAMA">DAMA (Discharge Against Medical Advice)</option>
                    <option value="Expired">Expired</option>
                    <option value="Referred">Referred</option>
                  </select>
                </Field>
                <Field label="Discharge Type">
                  <select style={styles.select} name="dischargeType" value={form.dischargeType} onChange={handleChange}>
                    <option value="Regular">Regular</option>
                    <option value="DAMA">DAMA</option>
                    <option value="Refer">Refer to Higher Centre</option>
                    <option value="Death">Death</option>
                  </select>
                </Field>
                {(form.conditionAtDischarge === 'Referred' || form.dischargeType === 'Refer') && (
                  <Field label="Referred To" fullWidth>
                    <input style={styles.input} name="referredTo" value={form.referredTo} onChange={handleChange} placeholder="Hospital / specialist name…" />
                  </Field>
                )}
              </div>
            </Section>

            {/* ── SECTION 6: Discharge Medications ─────────────────────── */}
            <Section title="6. Discharge Medications" icon="📦">
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.medTable}>
                  <thead>
                    <tr style={styles.medThead}>
                      <th style={styles.medTh}>#</th>
                      <th style={styles.medTh}>Drug Name</th>
                      <th style={styles.medTh}>Dose</th>
                      <th style={styles.medTh}>Frequency</th>
                      <th style={styles.medTh}>Duration</th>
                      <th style={styles.medTh}>Instructions</th>
                      <th style={styles.medTh}>✕</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medications.map((med, idx) => (
                      <tr key={idx} style={idx % 2 === 0 ? styles.medRowEven : styles.medRowOdd}>
                        <td style={styles.medTd}>{idx + 1}</td>
                        <td style={styles.medTd}>
                          <input style={styles.medInput} value={med.drug} onChange={(e) => handleMedChange(idx, 'drug', e.target.value)} placeholder="e.g. Amoxicillin" />
                        </td>
                        <td style={styles.medTd}>
                          <input style={styles.medInput} value={med.dose} onChange={(e) => handleMedChange(idx, 'dose', e.target.value)} placeholder="500mg" />
                        </td>
                        <td style={styles.medTd}>
                          <input style={styles.medInput} value={med.frequency} onChange={(e) => handleMedChange(idx, 'frequency', e.target.value)} placeholder="TDS" />
                        </td>
                        <td style={styles.medTd}>
                          <input style={styles.medInput} value={med.duration} onChange={(e) => handleMedChange(idx, 'duration', e.target.value)} placeholder="5 days" />
                        </td>
                        <td style={styles.medTd}>
                          <input style={styles.medInput} value={med.instructions} onChange={(e) => handleMedChange(idx, 'instructions', e.target.value)} placeholder="After food" />
                        </td>
                        <td style={{ ...styles.medTd, textAlign: 'center' }}>
                          <button
                            style={styles.removeBtn}
                            onClick={() => removeMedRow(idx)}
                            title="Remove row"
                            aria-label={`Remove medication row ${idx + 1}`}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button style={styles.addRowBtn} onClick={addMedRow}>
                + Add Medication Row
              </button>
            </Section>

            {/* ── SECTION 7: Follow-up Plan ─────────────────────────────── */}
            <Section title="7. Follow-up Plan" icon="📅">
              <div style={styles.grid2}>
                <Field label="Follow-up Date">
                  <input style={styles.input} type="date" name="followupDate" value={form.followupDate} onChange={handleChange} />
                </Field>
                <Field label="Follow-up Doctor">
                  <input style={styles.input} name="followupDoctor" value={form.followupDoctor} onChange={handleChange} placeholder="Dr. Sharma (Cardiology)" />
                </Field>
                <Field label="Instructions" fullWidth>
                  <textarea style={styles.textarea} name="followupInstructions" rows={3} value={form.followupInstructions} onChange={handleChange} placeholder="Specific instructions for follow-up visit…" />
                </Field>
                <Field label="Diet Advice" fullWidth>
                  <textarea style={styles.textarea} name="dietAdvice" rows={2} value={form.dietAdvice} onChange={handleChange} placeholder="Low salt, low fat, diabetic diet…" />
                </Field>
                <Field label="Activity Restrictions" fullWidth>
                  <textarea style={styles.textarea} name="activityRestrictions" rows={2} value={form.activityRestrictions} onChange={handleChange} placeholder="No heavy lifting for 4 weeks, bed rest…" />
                </Field>
              </div>
            </Section>

            {/* ── SECTION 8: Doctor Sign-off ────────────────────────────── */}
            <Section title="8. Doctor Sign-off" icon="✍️">
              <div style={styles.grid2}>
                <Field label="Prepared By">
                  <input style={styles.input} name="preparedBy" value={form.preparedBy} onChange={handleChange} placeholder="Dr. Name" />
                </Field>
                <Field label="Designation">
                  <input style={styles.input} name="designation" value={form.designation} onChange={handleChange} placeholder="Resident / Registrar" />
                </Field>
                <Field label="Reviewed By">
                  <input style={styles.input} name="reviewedBy" value={form.reviewedBy} onChange={handleChange} placeholder="Dr. Name (Consultant)" />
                </Field>
                <Field label="Review Designation">
                  <input style={styles.input} name="reviewDesignation" value={form.reviewDesignation} onChange={handleChange} placeholder="Consultant / HOD" />
                </Field>
                <Field label="Date of Summary">
                  <input style={styles.input} type="date" name="summaryDate" value={form.summaryDate} onChange={handleChange} />
                </Field>
              </div>

              {/* Signature boxes */}
              <div style={styles.sigRow}>
                <div style={styles.sigBox}>
                  <div style={styles.sigLine} />
                  <p style={styles.sigLabel}>Prepared By – {form.preparedBy || '_______________'}</p>
                  <p style={styles.sigSub}>{form.designation}</p>
                </div>
                <div style={styles.sigBox}>
                  <div style={styles.sigLine} />
                  <p style={styles.sigLabel}>Reviewed By – {form.reviewedBy || '_______________'}</p>
                  <p style={styles.sigSub}>{form.reviewDesignation}</p>
                </div>
              </div>
            </Section>

          </div>  /* end formWrap */
        )}

        {/* ── Action Bar ──────────────────────────────────────────────────── */}
        {selectedPatient && (
          <div style={styles.actionBar} className="action-bar">
            {savedMsg && <span style={styles.savedMsg}>{savedMsg}</span>}
            <button
              id="discharge-save-draft-btn"
              style={styles.btnSave}
              onClick={handleSaveDraft}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              type="button"
              style={{ ...styles.btnSave, borderColor: '#0d6e6a', color: '#0d6e6a' }}
              onClick={autoGenerate}
              disabled={saving}
              title="Pull EMR, labs, pharmacy into this summary"
            >
              ✨ Auto-fill from chart
            </button>
            {hospitalAiOn && (
              <button
                type="button"
                style={{ ...styles.btnSave, borderColor: '#4f46e5', color: '#4338ca' }}
                onClick={aiPolishDraft}
                disabled={saving}
                title="Rewrite narrative fields with AI (doctor must review)"
              >
                AI draft
              </button>
            )}
            <button
              type="button"
              style={{ ...styles.btnSave, borderColor: '#059669', color: '#059669' }}
              onClick={handleFinalize}
              disabled={saving}
            >
              Finalize Summary
            </button>
            <button
              id="discharge-print-btn"
              style={styles.btnPrint}
              onClick={handlePrint}
            >
              Print Summary
            </button>
          </div>
        )}

      </div>  {/* end rightPanel */}

    </div>  /* end pageWrap */
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

function Section({ title, icon, children }) {
  return (
    <div style={styles.section} className="discharge-section">
      <div style={styles.sectionTitleBar} className="section-title">
        <span style={styles.sectionIcon}>{icon}</span>
        <span>{title}</span>
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Field({ label, children, fullWidth = false }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined, marginBottom: 12 }}>
      <label style={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  STYLES
// ═════════════════════════════════════════════════════════════════════════════
const styles = {
  /* Layout */
  pageWrap: {
    display: 'flex',
    height: '100vh',
    background: '#f0f4f8',
    fontFamily: "'Segoe UI', Arial, sans-serif",
    overflow: 'hidden',
  },

  /* Left panel */
  leftPanel: {
    width: '30%',
    minWidth: 260,
    maxWidth: 340,
    background: '#1a2332',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #243047',
    overflow: 'hidden',
  },
  leftHeader: {
    padding: '18px 16px 12px',
    borderBottom: '1px solid #243047',
  },
  leftTitle: {
    margin: 0,
    color: '#e2e8f0',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 0.4,
  },
  searchWrap: {
    padding: '10px 12px',
  },
  searchInput: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #2d3e55',
    background: '#243047',
    color: '#e2e8f0',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  },
  patientList: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 10px 12px',
  },
  spinnerWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 40,
  },
  spinner: {
    width: 36,
    height: 36,
    border: '4px solid #2d3e55',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.9s linear infinite',
  },
  spinnerText: {
    marginTop: 12,
    color: '#7a8fa6',
    fontSize: 13,
  },
  emptyMsg: {
    color: '#7a8fa6',
    textAlign: 'center',
    marginTop: 40,
    fontSize: 13,
  },

  /* Patient card */
  patientCard: {
    background: '#243047',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.2s, background 0.2s',
  },
  patientCardActive: {
    border: '2px solid #3b82f6',
    background: '#1e3558',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  cardName: {
    color: '#e2e8f0',
    fontWeight: 600,
    fontSize: 14,
  },
  cardBadge: {
    background: '#3b82f6',
    color: '#fff',
    borderRadius: 5,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 700,
  },
  cardMeta: {
    display: 'flex',
    gap: 10,
    marginBottom: 3,
  },
  cardUhid: {
    color: '#7a8fa6',
    fontSize: 11,
  },
  cardWard: {
    color: '#60a5fa',
    fontSize: 11,
    fontWeight: 600,
  },
  cardDiag: {
    color: '#a5b4c5',
    fontSize: 12,
    marginBottom: 2,
  },
  cardAdmDate: {
    color: '#5a7a96',
    fontSize: 11,
  },

  /* Right panel */
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  placeholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
  },
  placeholderIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  placeholderTitle: {
    fontSize: 22,
    color: '#4a5568',
    marginBottom: 8,
    fontWeight: 700,
  },
  placeholderSub: {
    fontSize: 14,
    color: '#9aa5b4',
    maxWidth: 320,
    textAlign: 'center',
  },

  /* Form wrap */
  formWrap: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 28px',
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: 14,
  },
  formTitle: {
    margin: 0,
    fontSize: 22,
    color: '#1a2332',
    fontWeight: 800,
  },
  formSubtitle: {
    margin: '4px 0 0',
    color: '#64748b',
    fontSize: 13,
  },
  draftBadge: {
    background: '#dcfce7',
    color: '#166534',
    borderRadius: 6,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid #bbf7d0',
  },

  /* Section */
  section: {
    background: '#fff',
    borderRadius: 12,
    marginBottom: 18,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  sectionTitleBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: 700,
    fontSize: 14,
    color: '#1a2332',
    letterSpacing: 0.2,
  },
  sectionIcon: {
    fontSize: 16,
  },
  sectionBody: {
    padding: '14px 16px 6px',
  },

  /* Grid layouts */
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 18px',
  },
  grid1: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },

  /* Field */
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 7,
    fontSize: 13,
    color: '#1e293b',
    background: '#f8fafc',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 7,
    fontSize: 13,
    color: '#1e293b',
    background: '#f8fafc',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 7,
    fontSize: 13,
    color: '#1e293b',
    background: '#f8fafc',
    boxSizing: 'border-box',
    outline: 'none',
    cursor: 'pointer',
  },

  /* Medications table */
  medTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    marginBottom: 10,
  },
  medThead: {
    background: '#1a2332',
  },
  medTh: {
    color: '#e2e8f0',
    padding: '8px 10px',
    fontWeight: 600,
    textAlign: 'left',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  medTd: {
    padding: '5px 6px',
    borderBottom: '1px solid #e2e8f0',
    verticalAlign: 'middle',
  },
  medRowEven: {
    background: '#fff',
  },
  medRowOdd: {
    background: '#f8fafc',
  },
  medInput: {
    width: '100%',
    padding: '5px 7px',
    border: '1px solid #e2e8f0',
    borderRadius: 5,
    fontSize: 12,
    background: 'transparent',
    boxSizing: 'border-box',
    outline: 'none',
    minWidth: 80,
  },
  removeBtn: {
    background: '#fee2e2',
    border: 'none',
    borderRadius: 5,
    color: '#b91c1c',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    padding: '2px 8px',
    lineHeight: 1.4,
  },
  addRowBtn: {
    background: 'none',
    border: '2px dashed #3b82f6',
    color: '#3b82f6',
    borderRadius: 8,
    padding: '7px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },

  /* Signature */
  sigRow: {
    display: 'flex',
    gap: 32,
    marginTop: 20,
    paddingTop: 10,
  },
  sigBox: {
    flex: 1,
    textAlign: 'center',
  },
  sigLine: {
    borderTop: '1px solid #94a3b8',
    marginBottom: 6,
  },
  sigLabel: {
    margin: 0,
    fontSize: 12,
    fontWeight: 600,
    color: '#334155',
  },
  sigSub: {
    margin: '2px 0 0',
    fontSize: 11,
    color: '#64748b',
  },

  /* Action bar */
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '12px 28px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
  },
  savedMsg: {
    color: '#166534',
    background: '#dcfce7',
    border: '1px solid #bbf7d0',
    borderRadius: 7,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    marginRight: 8,
  },
  btnSave: {
    background: '#f1f5f9',
    border: '1.5px solid #3b82f6',
    color: '#1d4ed8',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  btnPrint: {
    background: '#1a2332',
    border: 'none',
    color: '#fff',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
};
